import React, { useEffect } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  /** Sticky action bar under the scrollable body */
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const sizeClass: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
};

const Modal: React.FC<ModalProps> = ({ open, onClose, title, children, footer, size = 'md' }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/45 backdrop-blur-[2px] safe-area-inset"
      dir="rtl"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`bg-white rounded-t-2xl sm:rounded-xl shadow-2xl shadow-slate-900/20 w-full max-w-[100vw] ${sizeClass[size]} max-h-[min(92dvh,40rem)] sm:max-h-[min(88vh,36rem)] overflow-hidden flex flex-col border border-slate-200/80`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3 shrink-0 bg-slate-50/80">
          <h3 className="text-sm sm:text-[0.95rem] font-bold text-slate-900 truncate flex-1 min-w-0 leading-snug">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="min-w-[36px] min-h-[36px] p-1.5 flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-200/70 active:bg-slate-200 rounded-lg transition-colors touch-manipulation shrink-0"
            aria-label="بستن"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-4 py-3.5 overflow-y-auto overflow-x-hidden flex-1 min-h-0 overscroll-contain">
          {children}
        </div>
        {footer != null && (
          <div className="px-4 py-3 border-t border-slate-100 bg-white shrink-0 flex flex-wrap items-center justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
